import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useTheme } from "./theme-context";

type DarkModeContextType = {
  isDark: boolean;
  toggle: () => void;
  adminEnabled: boolean;
  showToggle: boolean;
  /** "store" for storefront pages, "admin" for /admin and /platform-admin. */
  scope: "store" | "admin";
};

const DarkModeContext = createContext<DarkModeContextType>({
  isDark: false,
  toggle: () => {},
  adminEnabled: false,
  showToggle: false,
  scope: "store",
});

export function useDarkMode() {
  return useContext(DarkModeContext);
}

// Separate localStorage keys so the operator's admin-panel preference never
// leaks into the customer-facing storefront and vice versa. Merchants asked
// for the admin theme to be a private operator setting — flipping it inside
// the dashboard must NOT affect what shoppers see.
const STORE_KEY = "darkMode";       // legacy key, kept for storefront so existing visitors don't reset
const ADMIN_KEY = "darkMode:admin"; // operator-only preference

function detectScope(pathname: string): "store" | "admin" {
  return /^\/(admin|platform-admin)(\/|$)/.test(pathname) ? "admin" : "store";
}

export function DarkModeProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const [location] = useLocation();
  const scope = detectScope(location || "/");

  // Storefront state — driven by per-tenant merchant theme settings.
  const [storeIsDark, setStoreIsDark] = useState(false);
  const [storeEnabled, setStoreEnabled] = useState(false);
  const [storeToggleVisible, setStoreToggleVisible] = useState(false);

  // Admin/platform-admin state — always available to the operator, no merchant
  // gating. Default to LIGHT unless the operator explicitly toggled before;
  // we intentionally do NOT auto-follow `prefers-color-scheme` because
  // operators reported the dashboard silently turning dark on machines whose
  // OS was in dark mode, which they read as a bug ("لماذا تغيرت المنصة
  // للوضع الليلي دون طلب مني؟"). The toggle still works and the choice
  // persists per-browser.
  const [adminIsDark, setAdminIsDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(ADMIN_KEY);
    if (stored !== null) return stored === "true";
    return false;
  });

  const [storeLoaded, setStoreLoaded] = useState(false);

  // Resolve the storefront preference from merchant settings + visitor choice.
  useEffect(() => {
    if (!theme) return;
    const data: any = theme;
    const enabled = data.darkModeEnabled === "true" || data.darkModeEnabled === true;
    const toggleVisible = data.darkModeToggle === "true" || data.darkModeToggle === true;
    setStoreEnabled(enabled);
    setStoreToggleVisible(enabled && toggleVisible);

    if (!enabled) {
      setStoreIsDark(false);
      setStoreLoaded(true);
      return;
    }

    const userPref = localStorage.getItem(STORE_KEY);
    const mode = data.darkModeDefault || "light";
    if (userPref !== null) {
      setStoreIsDark(userPref === "true");
    } else if (mode === "dark") {
      setStoreIsDark(true);
    } else if (mode === "system") {
      setStoreIsDark(window.matchMedia("(prefers-color-scheme: dark)").matches);
    } else {
      setStoreIsDark(false);
    }
    setStoreLoaded(true);
  }, [theme]);

  // Apply the correct dark class for the *current* scope on every route or
  // state change. Without this, navigating from /admin (dark) to / (light)
  // would leave the storefront in dark mode and vice-versa.
  useEffect(() => {
    if (scope === "store" && !storeLoaded) return;
    const root = document.documentElement;
    const effectiveDark =
      scope === "admin" ? adminIsDark : storeIsDark && storeEnabled;
    if (effectiveDark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [scope, adminIsDark, storeIsDark, storeEnabled, storeLoaded]);

  // Persist whichever scope was just touched. Each scope writes to its own
  // key so toggling in one never overwrites the other.
  useEffect(() => {
    if (scope !== "store" || !storeLoaded) return;
    if (storeEnabled) localStorage.setItem(STORE_KEY, String(storeIsDark));
    else localStorage.removeItem(STORE_KEY);
  }, [scope, storeIsDark, storeEnabled, storeLoaded]);

  useEffect(() => {
    if (scope !== "admin") return;
    localStorage.setItem(ADMIN_KEY, String(adminIsDark));
  }, [scope, adminIsDark]);

  const toggle = () => {
    if (scope === "admin") {
      setAdminIsDark((p) => !p);
    } else {
      if (!storeEnabled) return;
      setStoreIsDark((p) => !p);
    }
  };

  const value: DarkModeContextType =
    scope === "admin"
      ? {
          isDark: adminIsDark,
          toggle,
          adminEnabled: true,
          showToggle: true,
          scope,
        }
      : {
          isDark: storeIsDark && storeEnabled,
          toggle,
          adminEnabled: storeEnabled,
          showToggle: storeToggleVisible,
          scope,
        };

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
}
